import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Search } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useSchool } from '../../contexts/SchoolContext';

interface StartDirectMessageModalProps {
  onClose: () => void;
  onSelectUser: (userId: string, userName: string) => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

export default function StartDirectMessageModal({ onClose, onSelectUser }: StartDirectMessageModalProps) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedSchool } = useSchool();

  useEffect(() => {
    const loadSchoolMembers = async () => {
      if (!selectedSchool || !auth.currentUser) return;

      try {
        // Get all school members
        const membersQuery = query(
          collection(db, 'school_members'),
          where('schoolId', '==', selectedSchool.id)
        );
        const memberSnapshot = await getDocs(membersQuery);
        
        // Get user details for each member (excluding current user)
        const userPromises = memberSnapshot.docs
          .filter(memberDoc => memberDoc.data().userId !== auth.currentUser?.uid)
          .map(async (memberDoc) => {
            const userDoc = await getDocs(
              query(
                collection(db, 'users'),
                where('__name__', '==', memberDoc.data().userId)
              )
            );
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              return {
                id: memberDoc.data().userId,
                name: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
                email: userData.email || ''
              };
            }
            return null;
          });

        const users = (await Promise.all(userPromises)).filter((user): user is UserOption => user !== null);
        setUsers(users.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Error loading school members:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSchoolMembers();
  }, [selectedSchool]);

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserSelect = (user: UserOption) => {
    onSelectUser(user.id, user.name);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-500" />
            <h2 className="text-xl font-semibold text-text-primary">Start Direct Message</h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-border-light">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search people..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-background rounded-lg border border-border-light 
                       focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="p-8 text-center">
              <div className="text-text-secondary">Loading people...</div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-text-secondary">
                {searchQuery ? 'No people found matching your search.' : 'No people available to message.'}
              </div>
            </div>
          ) : (
            <div className="p-2">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user)}
                  className="w-full p-3 rounded-lg text-left transition-colors hover:bg-background 
                           flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">{user.name}</p>
                    <p className="text-sm text-text-secondary truncate">{user.email}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}